import {
	QueryClient,
	QueryKey,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { t } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { APPLICATION_STATUS } from "../configs/constants/ApplicationStatus";
import { ApiErrorResponse } from "../models/ApiResponse";
import Consultant, {
	Absences,
	ConsultantRating,
	ConsultantSalarySummaryData,
	ConsultantsListResponse,
	ConsultantTag,
	CreateConsultantRequest,
	Salary,
	UpdateConsultantRequest,
} from "../models/Consultant";
import { ConsultantScheduleNew } from "../models/ConsultantSchedule";
import { TimelinePage } from "../models/EventTimeline";
import { BlockedCustomer } from "../models/Customer";
import { Application, Shift } from "../models/Job";
import { createUpdateConsultantReadableError } from "../utils/api.utils";
import { isUndefinedOrTrue } from "../utils/object.utils";
import { PERSIST_MAX_AGE, QueryOpts } from "./api-hooks";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";

export type GetConsultantsParams = {
	page: number;
	pageSize: number;
	keyword?: string;
	pool?: string;
	sort?: string;
	order?: string;
	expand?: string[];
	/** `select_option` returns every match (no paging) as id + names only. */
	view?: "select_option";
};

export function useConsultants(query: GetConsultantsParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["consultants", query],
		() =>
			apiStore
				.get<{ totalCount: number; users: Consultant[] }>({
					path: "/consultant-account",
					isAuthRequired: true,
					query: {
						...query,
						expand: query.expand ? query.expand.join(",") : "",
					},
				})
				.then((d) => d.data),
		{
			keepPreviousData: true,
			enabled,
		}
	);

	return {
		...rest,
		consultantList: data,
	};
}

/** Cached shape of the full consultant list, tagged with the server's sync version. */
export type AllConsultantsData = {
	syncVersion: number;
	items: ConsultantsListResponse[];
};

/** Current server-side sync version for consultant accounts (per org). */
export async function fetchConsultantSyncVersion(): Promise<number> {
	const { data } = await apiStore.get<{ version: number }>({
		path: "/consultant-account-sync-version",
		isAuthRequired: true,
	});

	return data.version;
}

/**
 * Page through the whole (slim) consultant list for an org/pool in large
 * batches. Most orgs fit in a single 5000-row request; the loop only kicks in
 * for larger ones. The endpoint already returns a projected list shape.
 *
 * The sync version is deliberately fetched BEFORE paging: if a write lands
 * while we page, the stored version looks old and the watcher triggers one
 * extra refetch — never staleness the other way around.
 */
async function fetchAllConsultants(pool?: string): Promise<AllConsultantsData> {
	const syncVersion = await fetchConsultantSyncVersion();

	const batchSize = 5000;
	const all: ConsultantsListResponse[] = [];
	let page = 1;
	let total = Infinity;

	while (all.length < total) {
		const { data } = await apiStore.get<{
			totalCount: number;
			users: ConsultantsListResponse[];
		}>({
			path: "/consultant-account",
			isAuthRequired: true,
			query: {
				pool: pool || undefined,
				page,
				pageSize: batchSize,
				includeCompetences: "true",
				includeProfileFields: "true",
				includeAvailability: "true",
				// Home town only (profile.address.city) - not the full address.
				includeCity: "true",
			},
		});

		const users = data.users || [];
		all.push(...users);
		total = data.totalCount ?? all.length;

		if (users.length < batchSize) break;
		page++;
	}

	return { syncVersion, items: all };
}

/**
 * Bump when the fields requested by fetchAllConsultants change. It is part of
 * the query key, so a persisted list fetched with the old shape is refetched
 * instead of served without the new fields — the deploy buster covers this in
 * production, but not for a local dev build ("dev" never changes) or when a
 * fresh build reuses an already-hydrated cache.
 */
const CONSULTANT_LIST_SHAPE = 2;

/**
 * Full consultant list for a pool, cached by React Query and persisted to
 * IndexedDB (see shouldDehydrateQuery in App.tsx). Intended for the
 * virtualized list, which filters/searches/sorts client-side over the result.
 * The list never goes stale on its own (staleTime: Infinity) — instead
 * useSyncVersionWatcher invalidates it when the server-side sync version
 * changes. cacheTime must match the persistence window: with the default
 * 5-min cacheTime the list would be garbage-collected once unmounted and the
 * persister would then write a cache WITHOUT the list.
 *
 * Every consumer that patches/reads this cache matches on the
 * ["consultants", "all"] prefix, so the trailing pool + shape entries are safe.
 */
export function useAllConsultants(pool?: string, enabled = true) {
	const { data, ...rest } = useQuery(
		["consultants", "all", pool ?? "", CONSULTANT_LIST_SHAPE],
		() => fetchAllConsultants(pool),
		{
			staleTime: Infinity,
			cacheTime: PERSIST_MAX_AGE,
			enabled,
		}
	);

	return {
		...rest,
		consultants: data?.items ?? [],
	};
}

/** One busy consultant as returned by `GET /booked-consultant-ids`. */
type BookedConsultant = {
	consultantId: string;
	/** YYYY-MM-DD dates within the requested range the consultant is busy on. */
	bookedDates: string[];
};

/**
 * Which consultants in the organisation are busy — booked on a shift or absent
 * — on which calendar days within [from, to] (YYYY-MM-DD, inclusive). Only busy
 * consultants come back, so a consultant missing from the map is free.
 *
 * Deliberately a side query rather than a parameter on the consultant list:
 * that list is cached in IndexedDB keyed by the org's sync version and must
 * stay time-independent, while bookings change by the minute. `staleTime` keeps
 * it off the wire while the user tweaks other filters.
 */
export function useBookedConsultantIds(from?: string, to?: string, enabled = true) {
	const { data, isInitialLoading, isError } = useQuery(
		["booked-consultant-ids", from ?? "", to ?? ""],
		() =>
			apiStore
				.get<{ bookedConsultants: BookedConsultant[] }>({
					path: "/booked-consultant-ids",
					isAuthRequired: true,
					query: { from, to },
				})
				.then((d) => d.data),
		{
			staleTime: 60 * 1000,
			enabled: enabled && !!from && !!to,
			onError: ({ error }: ApiErrorResponse) => {
				toast.apiError(error.code);
			},
		}
	);

	// Undefined until the range has actually been fetched: callers must be able to
	// tell "nobody is busy" from "we don't know yet", so a failed or pending fetch
	// never reads as everyone being available.
	const busyDatesByConsultant = useMemo(
		() =>
			data &&
			new Map(
				data.bookedConsultants.map(
					({ consultantId, bookedDates }) => [consultantId, new Set(bookedDates)] as const
				)
			),
		[data]
	);

	return { busyDatesByConsultant, isLoading: isInitialLoading, isError };
}

type GetConsultantParams = {
	consultantId?: string;
	expand?: string[];
	include?: string[];
};

export function useConsultant(params: GetConsultantParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["consultants", params.consultantId, params],
		() =>
			apiStore
				.get<Consultant>({
					path: "/consultant-account/" + params.consultantId,
					isAuthRequired: true,
					query: {
						expand: params.expand ? params.expand.join(",") : "",
						include: params.include ? params.include.join(",") : "",
					},
				})
				.then((d) => {
					return d.data;
				}),
		{
			enabled: !!params.consultantId && isUndefinedOrTrue(enabled),
		}
	);

	return {
		...rest,
		consultant: data,
	};
}

type GetConsultantApplicationsParams = {
	consultantId?: string;
	status?: APPLICATION_STATUS[];
	expand?: string[];
	pageSize?: number;
	page?: number;
	startDate?: string;
	endDate?: string;
	includeOverlappingRange?: boolean;
};

export function useConsultantApplications<T extends Application>(
	params: GetConsultantApplicationsParams,
	enabled?: boolean
) {
	const { consultantId, status, expand, ...query } = params;

	return useQuery(
		["consultants", consultantId, query],
		() =>
			apiStore
				.get<{ applications: T[] }>({
					path: `/user/${consultantId}/application`,
					isAuthRequired: true,
					query: {
						...query,
						status: status ? status.join(",") : "",
						expand: expand ? expand.join(",") : "",
					},
				})
				.then((d) => d.data.applications),
		{
			enabled,
		}
	);
}

export function useConsultantShifts({
	consultantId,
	start,
	end,
}: {
	consultantId: string;
	start?: string | Date;
	end?: string | Date;
}) {
	return useQuery(["consultants", consultantId, "shifts", { start, end }], () =>
		apiStore
			.get<{ shifts: Shift[] }>({
				path: `/user/${consultantId}/shift`,
				isAuthRequired: true,
				query: {
					start: start ? new Date(start).toISOString() : start,
					end: end ? new Date(end).toISOString() : end,
				},
			})
			.then((d) => d.data.shifts)
	);
}

export type GetConsultantScheduleParams = {
	startDate: string | Date;
	endDate: string | Date;
	pool?: string;
	expand?: string[];
	customerId?: string;
	contractId?: string;
};

export function useConsultantSchedule(
	startTime: string,
	endTime: string,
	customerIds?: string[],
	contractIds?: string[],
	expand?: string[],
	include?: string[],
	enable: boolean = true,
	consultantManagerId?: string,
	includeUnbooked?: boolean
) {
	if (!customerIds || !enable) {
		return { consultantSchedules: [], isLoading: false };
	}

	const { data } = useQuery(
		[
			"useConsultantSchedule",
			startTime,
			endTime,
			enable,
			JSON.stringify(customerIds),
			JSON.stringify(contractIds),
			JSON.stringify(expand),
			JSON.stringify(include),
			consultantManagerId,
			includeUnbooked,
		],
		() =>
			apiStore
				.get<{ consultantSchedules: ConsultantScheduleNew[] }>({
					path: "/schedule/consultant",
					query: {
						startTime,
						endTime,
						customerIds: customerIds ? customerIds.join(",") : "",
						contractIds: contractIds ? contractIds.join(",") : "",
						expand: expand ? expand.join(",") : "",
						include: include ? include.join(",") : "",
						consultantManagerId: consultantManagerId || undefined,
						includeUnbooked: includeUnbooked || undefined,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data)
	);
	return { ...data };
}

export function useUpdateConsultant() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			userId: string;
			silent?: boolean;
			update: Partial<UpdateConsultantRequest>;
			optimisticUpdate?: boolean;
		}
	>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.userId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				// Patch the persisted full lists in place so navigating back to the
				// list shows the change immediately instead of after a full re-page.
				// Only the submitted fields are merged — list rows are slim
				// projections, so a fat server response must not be spread in.
				// syncVersion is deliberately left untouched: the server bumped it
				// on this write, so the sync-version watcher reconciles the list
				// (incl. any server-side divergence or concurrent writes by others)
				// in the background.
				queryClient.setQueriesData<AllConsultantsData>(["consultants", "all"], (old) =>
					old && Array.isArray(old.items)
						? {
								...old,
								items: old.items.map((c) =>
									c.id === params.userId
										? {
												...c,
												...params.update,
												profile: { ...c.profile, ...params.update.profile },
										  }
										: c
								),
						  }
						: old
				);

				if (params.optimisticUpdate) {
					// Patch the cached consultant in place (no refetch) so reads
					// like the header availability summary reflect the change
					// immediately. The ["consultants", userId] prefix also matches
					// sibling caches (shifts/applications arrays), so only patch
					// entries that are actually a consultant object.
					queryClient.setQueriesData<Consultant>(["consultants", params.userId], (old) =>
						old && !Array.isArray(old) && (old as Consultant).profile
							? {
									...old,
									...params.update,
									profile: { ...old.profile, ...params.update.profile },
							  }
							: old
					);
				} else {
					// Namespace invalidation: refreshes the consultant detail and the
					// paged lists. The full "all" lists are excluded — they were just
					// patched above and re-paging thousands of rows here would defeat
					// the persisted cache; the sync-version watcher reconciles them.
					queryClient.invalidateQueries({
						queryKey: ["consultants"],
						predicate: (query) => query.queryKey[1] !== "all",
					});
				}
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(createUpdateConsultantReadableError(error.code));
				}
			},
		}
	);
}

/**
 * Set a consultant's tags with a true optimistic update, so toggling a tag from
 * e.g. the contact context menu feels instant instead of waiting for the full
 * consultant list to refetch.
 *
 * Safe to patch optimistically because the client computes the exact final
 * `profile.tags` array (see `toggleConsultantTag`) and the backend stores it
 * verbatim for admins/consultant managers. All caches that render tags are
 * patched in `onMutate` and rolled back on error; `onSuccess` writes the
 * consultant returned by the PUT back into the caches so any server-side
 * divergence (e.g. tags stripped for a role without permission) self-corrects
 * without a refetch. The consultants namespace is only marked stale
 * (`refetchType: "none"`) so the mounted full-list query doesn't re-page
 * thousands of rows on every toggle; it reconciles on the next mount/focus.
 */
export function useToggleConsultantTag() {
	const queryClient = useQueryClient();

	const patchCaches = (userId: string, tags: ConsultantTag[]) => {
		// The contact context menu's submenu checkboxes read the contact summary.
		queryClient.setQueryData<{ tags?: ConsultantTag[] }>(["user-contact-summary", userId], (old) =>
			old ? { ...old, tags } : old
		);

		// The full per-pool lists backing the virtualized list's tag chips.
		queryClient.setQueriesData<AllConsultantsData>(["consultants", "all"], (old) =>
			old && Array.isArray(old.items)
				? {
						...old,
						items: old.items.map((c) =>
							c.id === userId ? { ...c, profile: { ...c.profile, tags } } : c
						),
				  }
				: old
		);

		// Consultant detail caches. The ["consultants", userId] prefix also matches
		// sibling caches (shifts/applications arrays), so only patch entries that
		// are actually a consultant object (same guard as useUpdateConsultant).
		queryClient.setQueriesData<Consultant>(["consultants", userId], (old) =>
			old && !Array.isArray(old) && (old as Consultant).profile
				? { ...old, profile: { ...old.profile, tags } }
				: old
		);
	};

	return useMutation<
		Consultant,
		ApiErrorResponse,
		{ userId: string; nextTags: ConsultantTag[] },
		{ entries: [QueryKey, unknown][] }
	>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.userId}`,
					body: { profile: { tags: params.nextTags } },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onMutate: async ({ userId, nextTags }) => {
				// Stop in-flight refetches from overwriting the optimistic patch.
				await queryClient.cancelQueries(["consultants"]);
				await queryClient.cancelQueries(["user-contact-summary", userId]);

				// Snapshot every cache we are about to patch, for rollback on error.
				const entries: [QueryKey, unknown][] = [
					...queryClient.getQueriesData(["consultants", "all"]),
					...queryClient.getQueriesData(["consultants", userId]),
					...queryClient.getQueriesData(["user-contact-summary", userId]),
				];

				patchCaches(userId, nextTags);

				return { entries };
			},
			onError: ({ error }, _params, context) => {
				context?.entries.forEach(([key, data]) => queryClient.setQueryData(key, data));
				toast.apiError(error.code);
			},
			onSuccess: (updated, { userId }) => {
				if (updated?.profile) {
					patchCaches(userId, updated.profile.tags ?? []);
				}
			},
			onSettled: (_data, _error, { userId }) => {
				// Mark stale without refetching mounted queries — the full list would
				// otherwise re-page thousands of rows on every toggle.
				queryClient.invalidateQueries({ queryKey: ["consultants"], refetchType: "none" });
				queryClient.invalidateQueries({
					queryKey: ["user-contact-summary", userId],
					refetchType: "none",
				});
			},
		}
	);
}

/**
 * Create a consultant account. Toast-silent (mirrors useCreateCustomer's
 * caller-owned flow): the create page shows a confirm modal on success and its
 * own error toast, so the hook only invalidates the consultant lists.
 */
export function useCreateConsultant() {
	const queryClient = useQueryClient();

	return useMutation<Consultant, ApiErrorResponse, CreateConsultantRequest>(
		(body) =>
			apiStore
				.post({
					path: `/consultant-account`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["consultants"]);
			},
		}
	);
}

export function useDeleteConsultant() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, { userId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.delete({
					path: `/user/${params.userId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}
				// Refresh both the detail caches and any consultant list.
				queryClient.invalidateQueries(["consultants"]);
			},
			onError: ({ error }, params) => {
				if (!params.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

const blockedCustomersKey = (consultantId?: string) => [
	"consultants",
	consultantId,
	"blocked-customers",
];

/** Customers a consultant is blocked from working for. */
export function useBlockedCustomers(consultantId?: string) {
	const { data, ...rest } = useQuery(
		blockedCustomersKey(consultantId),
		() =>
			apiStore
				.get<{ blocks: BlockedCustomer[] }>({
					path: `/consultant-account/${consultantId}/block`,
					query: { expand: "customer,metadata.createdBy", pageSize: 100 },
					isAuthRequired: true,
				})
				.then((d) => d.data.blocks),
		{ enabled: !!consultantId }
	);

	return {
		blockedCustomers: data ?? [],
		...rest,
	};
}

export function useBlockCustomer() {
	const queryClient = useQueryClient();

	return useMutation<
		unknown,
		ApiErrorResponse,
		{ consultantId: string; customerId: string; comment?: string }
	>(
		(params) =>
			apiStore
				.post({
					path: `/consultant-account/${params.consultantId}/block`,
					body: { customer: { id: params.customerId }, comment: params.comment },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				queryClient.invalidateQueries(blockedCustomersKey(params.consultantId));
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useUnblockCustomer() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, { consultantId: string; customerId: string }>(
		(params) =>
			apiStore
				.delete({
					path: `/consultant-account/${params.consultantId}/block`,
					body: { customer: { id: params.customerId } },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				queryClient.invalidateQueries(blockedCustomersKey(params.consultantId));
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

type GetConsultantAbsenceParams = {
	consultantId?: string;
};

export function useConsultantAbsence(params: GetConsultantAbsenceParams, enabled?: boolean) {
	const { data, isInitialLoading } = useQuery(
		["consultants", params.consultantId, "absence", params],
		() =>
			apiStore
				.get<{ absences: Absences[] }>({
					path: `/consultant-account/${params.consultantId}/absence`,
					query: {
						expand: "metadata.createdBy",
						pageSize: 10000,
						sort: "startDate",
						order: "-1",
					},
					isAuthRequired: true,
				})
				.then((res) => res.data),
		{ enabled: !!params.consultantId && isUndefinedOrTrue(enabled) }
	);

	return {
		absences: data?.absences,
		isLoading: isInitialLoading,
		...data,
	};
}

export interface CreateAbsencesRequest {
	startDate: string;
	endDate: string;
	type: string;
}

export function useCreateAbsence() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			consultantId: string;
			silent?: boolean;
			body: CreateAbsencesRequest;
		}
	>(
		(params) =>
			apiStore
				.post({
					path: `/consultant-account/${params.consultantId}/absence`,
					body: params.body,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["consultants", params.consultantId, "absence"]);
				queryClient.invalidateQueries(["time-reports"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDeleteFeedback() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			consultantId: string;
			ratingId: string;
			silent?: boolean;
			callback: () => void;
		}
	>(
		(params) =>
			apiStore
				.delete({
					path: `/consultant-account/${params.consultantId}/rating/${params.ratingId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.success(t("jobReportFeedback.deleted"));
				}

				queryClient.invalidateQueries(["consultant-ratings", params.consultantId, "rating"]);
				params.callback();
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useUpdateAbsence() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			consultantId: string;
			absenceId: string;
			silent?: boolean;
			update: { endDate: Absences["endDate"] };
		}
	>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.consultantId}/absence/${params.absenceId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["consultants", params.consultantId, "absence"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useDeleteAbsence() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			consultantId: string;
			absenceId: string;
			silent?: boolean;
			force?: boolean;
		}
	>(
		(params) =>
			apiStore
				.delete({
					path: `/consultant-account/${params.consultantId}/absence/${params.absenceId}`,
					query: params.force ? { force: "true" } : undefined,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				queryClient.invalidateQueries(["consultants", params.consultantId, "absence"]);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}
interface UserContactSummary {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber?: string;
	role: "customer_user" | "consultant";
	isCandidate?: boolean;
	employeeNumber?: number;
	/**
	 * Org-defined tags assigned to the consultant. Undefined for customer users
	 * and when the backend predates this field (guards the tags submenu so a
	 * dashboard-before-backend deploy can't accidentally wipe tags).
	 */
	tags?: {
		key: string;
		assignedAt: string;
		expiresAt?: string | null;
	}[];
	customers?: {
		id: string;
		name: string;
		customerNumber: string;
	}[];
}

export function useUserContactSummary(userId?: string) {
	const { data, ...rest } = useQuery(
		["user-contact-summary", userId],
		() =>
			apiStore
				.get<UserContactSummary>({
					path: `/user/${userId}/contact-summary`,
					isAuthRequired: true,
				})
				.then((res) => res.data),
		{ enabled: !!userId, refetchOnWindowFocus: false }
	);

	return {
		contactSummary: data,
		...rest,
	};
}

export interface UserLastActivity {
	appVersion: string;
	date: string;
	event: string;
	id: string;
	otaVersion: string;
	platform: "android" | "ios" | "web";
	roles: string[];
	userId: string;
}

export function useUserLastActivity(userId?: string) {
	const { data, ...rest } = useQuery(
		["user-last-activity", userId],
		() =>
			apiStore
				.get<UserLastActivity>({
					path: `/user/${userId}/last_activity`,
					isAuthRequired: true,
				})
				.then((res) => res.data),
		{ enabled: !!userId, refetchOnWindowFocus: false }
	);

	return {
		lastActivity: data,
		...rest,
	};
}
interface SendMessageRequest {
	channels: {
		mail: boolean;
		push: boolean;
		sms: boolean;
	};
	title: string;
	message: string;
	users: { id: string }[];
}

export function useSendUserMessage() {
	return useMutation<any, ApiErrorResponse, { silent?: boolean; body: SendMessageRequest }>(
		(params) =>
			apiStore
				.post({
					path: `/user/message`,
					body: params.body,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

/**
 * Shared onSuccess for activate/inactivate: flips `active` on the consultant's
 * row in every persisted full list (so the virtualized list and its
 * active-filter reflect the change immediately) and on the detail caches, then
 * invalidates the rest of the namespace. Mirrors useUpdateConsultant:
 * syncVersion is left untouched on purpose — the server bumped it on this
 * write, so the sync-version watcher reconciles the lists in the background.
 */
function patchConsultantActiveCaches(queryClient: QueryClient, userId: string, active: boolean) {
	queryClient.setQueriesData<AllConsultantsData>(["consultants", "all"], (old) =>
		old && Array.isArray(old.items)
			? {
					...old,
					items: old.items.map((c) => (c.id === userId ? { ...c, active } : c)),
			  }
			: old
	);

	// Consultant detail caches. The ["consultants", userId] prefix also matches
	// sibling caches (shifts/applications arrays), so only patch entries that
	// are actually a consultant object (same guard as useUpdateConsultant).
	queryClient.setQueriesData<Consultant>(["consultants", userId], (old) =>
		old && !Array.isArray(old) && (old as Consultant).profile ? { ...old, active } : old
	);

	// Refresh everything else in the namespace — including the detail, since
	// the backend may change more than the active flag; the patch above just
	// bridges until that refetch lands. Only the full "all" lists are
	// excluded: they were just patched and re-paging thousands of rows here
	// would defeat the persisted cache.
	queryClient.invalidateQueries({
		queryKey: ["consultants"],
		predicate: (query) => query.queryKey[1] !== "all",
	});
}

export function useActivateConsultant() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.userId}/active`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				patchConsultantActiveCaches(queryClient, params.userId, true);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export function useInactivateConsultant() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string; silent?: boolean }>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.userId}/inactive`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				if (!params.silent) {
					toast.successGeneric();
				}

				patchConsultantActiveCaches(queryClient, params.userId, false);
			},
			onError: ({ error }, data) => {
				if (!data.silent) {
					toast.apiError(error.code);
				}
			},
		}
	);
}

export interface SalaryTimeReport {
	sickHourlyPay: number;
	absence: Absences;
	total: number;
	reportedTime: number;
	hourlyPay: number;
}

export function useConsultantSalarySummary(
	consultantId: string,
	startTime: string,
	endTime: string
) {
	const { data, ...rest } = useQuery(
		["consultant-salary-summary", consultantId, startTime, endTime],
		() =>
			apiStore
				.get<ConsultantSalarySummaryData>({
					path: `/consultant/${consultantId}/salary_summary`,
					isAuthRequired: true,
					query: { startTime, endTime },
				})
				.then((res) => res.data),
		{ enabled: !!consultantId, refetchOnWindowFocus: false }
	);

	return {
		salary: { ...data },
		...rest,
	};
}

export function useConsultantRatings(
	startTime: string,
	endTime: string,
	consultantIds?: string[],
	expand?: string[],
	enabled?: boolean
) {
	const { data, ...rest } = useQuery(
		["consultant-ratings", consultantIds, startTime, endTime, expand],
		() =>
			apiStore
				.get<{ ratings: ConsultantRating[] }>({
					path: `/consultant_rating`,
					isAuthRequired: true,
					query: {
						startTime,
						endTime,
						consultantIds: consultantIds ? consultantIds.join(",") : "",
						expand: expand ? expand.join(",") : "",
					},
				})
				.then((res) => res.data),
		{ enabled: !!(startTime && endTime) && isUndefinedOrTrue(enabled), refetchOnWindowFocus: false }
	);

	return {
		...data,
		...rest,
	};
}

export function useUpdateSalary() {
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{
			consultantId: string;
			silent?: boolean;
			update: Partial<Salary>;
		}
	>(
		(params) =>
			apiStore
				.put({
					path: `/consultant/${params.consultantId}/salary`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: (_, params) => {
				queryClient.invalidateQueries(["consultants", params.consultantId]);
				if (!params.silent) {
					toast.successGeneric();
				}
			},
		}
	);
}

export function useSetConsultantPassword() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string; newPassword: string }>(
		(params) =>
			apiStore
				.put({
					path: `/consultant-account/${params.userId}/password`,
					body: { newPassword: params.newPassword },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				queryClient.invalidateQueries(["consultants", params.userId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useGeneratePresentation() {
	return useMutation<
		{
			presentation: string;
		},
		ApiErrorResponse,
		{
			userId: string;
		}
	>(
		(params) =>
			apiStore
				.get<{ presentation: string }>({
					path: `/consultant-account/${params.userId}/generate-presentation`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useSendConsultantContract() {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	return useMutation<
		any,
		ApiErrorResponse,
		{ userId: string; sms: boolean; mail: boolean; expireDate?: string }
	>(
		(params) =>
			apiStore
				.post({
					path: `/consultant-account/${params.userId}/send-contract`,
					isAuthRequired: true,
					body: {
						channels: {
							mail: true,
							sms: params.sms,
						},
						expireDate: params.expireDate,
					},
				})
				.then((d) => d.data),
		{
			onSuccess: (_, data) => {
				toast.success(t("consultant.sendContractSuccess"));
				queryClient.invalidateQueries(["consultant", data.userId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

export function useRecallConsultantAgreement() {
	const queryClient = useQueryClient();

	return useMutation<any, ApiErrorResponse, { userId: string }>(
		(params) =>
			apiStore
				.delete({
					path: `/consultant-account/${params.userId}/contract`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: (_, params) => {
				toast.successGeneric();
				queryClient.invalidateQueries(["consultant", params.userId]);
			},
			onError: ({ error }) => {
				toast.apiError(error.code);
			},
		}
	);
}

/**
 * The consultant event timeline ("Händelser"), newest first, one page at a
 * time. Fetched by the history tab on activation.
 *
 * Paged with an opaque cursor rather than an offset: entries written by one
 * save share a timestamp, so the backend pages on a (date, id) tuple and hands
 * back the cursor to continue from.
 *
 * `includeMessages` merges the sent-notification log into the same page. It is
 * part of the query key so a change of flag starts a fresh paging sequence
 * rather than appending a differently-sourced page to the one already cached.
 */
export function useConsultantHistory(
	{ userId, includeMessages }: { userId?: string; includeMessages?: boolean },
	opts: QueryOpts = {}
) {
	const infinite = useInfiniteQuery(
		["consultants", userId, "history", { includeMessages: !!includeMessages }],
		({ pageParam }) => {
			const query: { cursor?: string; includeMessages?: boolean } = {};

			if (pageParam) query.cursor = pageParam;
			if (includeMessages) query.includeMessages = true;

			return apiStore
				.get<TimelinePage>({
					path: `/consultant-account/${userId}/history`,
					isAuthRequired: true,
					query: Object.keys(query).length ? query : undefined,
				})
				.then((d) => d.data);
		},
		{
			getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.nextCursor : undefined),
			enabled: !!userId && isUndefinedOrTrue(opts.enabled),
			...opts,
		}
	);

	const entries = useMemo(
		() => infinite.data?.pages.flatMap((page) => page?.entries ?? []),
		[infinite.data]
	);

	return { entries, ...infinite };
}
